import ImageEntryModel from './ImageEntryModel.svelte.js';

export default class CollectionEntryModel {
  // system-native props
  entry_id = $state();
  type = $state();
  slug = $state();
  created_at = $state();
  updated_at = $state();
  user_id = $state();
  visibility = $state();
  preview_title = $state();
  preview_preamble = $state();
  preview_description = $state();
  preview_images_multiref = $state();
  preview_images = $state();
  tag_keys = $state([]);
  exclude_from_search = $state(false); // hidden from site search, sitemap and search engines
  // type-specific custom props
  page_layout = $state();
  viewer_available_layouts = $state();
  entry_previews_layout = $state();
  preamble = $state();
  title = $state();
  description = $state();
  allow_enquiry = $state();
  entry_previews_multiref = $state();
  entry_previews = $state();
  // derived info
  preview = $derived(this.to_preview());
  hidden_entry_previews_count = $derived.by(() => {
    const total = Array.isArray(this.entry_previews_multiref) ? this.entry_previews_multiref.length : 0;
    const visible = Array.isArray(this.entry_previews) ? this.entry_previews.length : 0;
    const hidden = total - visible;
    return hidden > 0 ? hidden : 0;
  });

  constructor({
    entry_id,
    type,
    slug,
    created_at,
    updated_at,
    user_id,
    visibility,
    preview_title,
    preview_preamble,
    preview_description,
    preview_images_multiref,
    preview_images, // computed from preview_images_multiref
    tag_keys,
    exclude_from_search,
    preamble,
    title,
    description,
    allow_enquiry,
    entry_previews_multiref,
    entry_previews,
    entry_previews_layout,
    page_layout,
    viewer_available_layouts
  } = {}) {
    // system-native props
    this.entry_id = entry_id,
    this.type = type;
    this.slug = slug;
    this.created_at = created_at;
    this.updated_at = updated_at;
    this.user_id = user_id;
    // NOTE: The defaults (based on user roles) are set in new/[type]/+page.server.js. However, we fall back to private as that's the most secure.
    this.visibility = visibility ?? 'private';
    this.preview_title = preview_title ?? '';
    this.preview_preamble = preview_preamble ?? '';
    this.preview_description = preview_description ?? '';
    this.preview_images_multiref = preview_images_multiref ?? [];
    this.preview_images = preview_images?.map(i => new ImageEntryModel(i)) ?? [];
    this.tag_keys = tag_keys ?? [];
    this.exclude_from_search = exclude_from_search ?? false;

    // type-specific custom props
    this.entry_previews_layout = entry_previews_layout ?? 1;
    this.page_layout = page_layout ?? 1;
    this.viewer_available_layouts = viewer_available_layouts ?? [1, 2];
    this.preamble = preamble ?? '';
    this.title = title ?? '';
    this.description = description ?? '';
    this.allow_enquiry = Boolean(allow_enquiry ?? true);
    this.entry_previews_multiref = entry_previews_multiref ? JSON.parse(JSON.stringify(entry_previews_multiref)) : [];
    // If entry_previews are resolved server-side, use them to avoid flash of content on page load.
    if (entry_previews?.length > 0) {
      this.entry_previews = $state.snapshot(entry_previews);
    } else {
      this.entry_previews = [];
    }
  }

  to_preview() {
    return {
      // system-native props
      entry_id: this.entry_id,
      type: this.type,
      slug: this.slug,
      preamble: this.preview_preamble || this.preamble,
      title: this.preview_title || this.title,
      description: this.preview_description || this.description,
      // A chosen preview image (e.g. an artist's portrait) wins over the first items, as for pages
      images: this.preview_images?.length > 0
        ? this.preview_images.map(i => i.to_json())
        : this.entry_previews.map(ep => ep.images?.[0]).filter(Boolean).slice(0, 3),
      // Cards show the second chosen preview image while they are pointed at, as for pages
      hover_image: this.preview_images?.[1]?.to_json(),
      tag_keys: this.tag_keys,
      created_at: this.created_at,
      updated_at: this.updated_at,
      visibility: this.visibility,
    };
  }

  to_json() {
    return {
      entry_id: this.entry_id,
      type: this.type,  
      visibility: this.visibility,
      preview_title: this.preview_title,
      preview_preamble: this.preview_preamble,
      preview_description: this.preview_description,
      preview_images_multiref: this.preview_images_multiref,
      tag_keys: this.tag_keys,
      exclude_from_search: this.exclude_from_search || undefined,
      allow_enquiry: this.allow_enquiry,
      entry_previews_layout: this.entry_previews_layout,
      page_layout: this.page_layout,
      viewer_available_layouts: this.viewer_available_layouts,
      preamble: this.preamble,
      title: this.title,
      description: this.description,
      entry_previews_multiref: this.entry_previews_multiref,
    };
  }

  toJSON() {
    return this.to_json();
  }
}
